// Seed the `gmSeats` store for a league.
//
// Called from two places that have very different transactions in hand:
//   1. `createStream.ts` — new league creation, gameAttributes + teams are
//      already populated, runs in a fresh readwrite tx that includes those
//      stores. The caller passes `userTid` directly.
//   2. `connectLeague.ts` migrate (oldVersion < 72) — schema upgrade, runs
//      inside the versionchange tx. The caller passes the existing
//      versionchange tx and `userTid` (read from gameAttributes earlier
//      in the same tx).
//
// Solo leagues end up with the single user-controlled seat at the user's
// tid (`userId = DEFAULT_USER_ID`) and AI seats (`userId = null`) for every
// other team. Phase 5 only seats the user — future phases (multi-GM, auth)
// add real userIds.

import type {
	IDBPTransaction,
	StoreNames,
	IDBPDatabase,
} from "@dumbmatter/idb";
import { DEFAULT_USER_ID } from "../../../common/wsProtocol.ts";
import type { LeagueDB } from "../../../common/types/db.ts";

type AnyLeagueTx = IDBPTransaction<
	LeagueDB,
	StoreNames<LeagueDB>[],
	"versionchange" | "readwrite"
>;

/**
 * Read all tids from the `teams` store using `tx` and write one `gmSeat` per
 * tid. The seat for `userTid` is owned by `DEFAULT_USER_ID`; every other
 * tid gets `userId: null` (AI). Existing rows in `gmSeats` are overwritten,
 * so calling this twice is idempotent.
 */
export const initGmSeatsViaTransaction = async (
	tx: AnyLeagueTx,
	userTid: number,
): Promise<number> => {
	const teamsStore = tx.objectStore("teams");
	const seatsStore = tx.objectStore("gmSeats");
	const teams = await teamsStore.getAll();
	const now = Date.now();
	let count = 0;
	for (const t of teams) {
		await seatsStore.put({
			tid: t.tid,
			userId: t.tid === userTid ? DEFAULT_USER_ID : null,
			joinedAt: now,
			active: true,
		});
		count += 1;
	}
	return count;
};

/**
 * Convenience for callers that don't have a tx open yet (e.g., a one-off
 * repair script). Opens its own readwrite tx over `teams` + `gmSeats`.
 */
export const initGmSeats = async (
	db: IDBPDatabase<LeagueDB>,
	userTid: number,
): Promise<number> => {
	const tx = db.transaction(["teams", "gmSeats"], "readwrite");
	const count = await initGmSeatsViaTransaction(tx, userTid);
	await tx.done;
	return count;
};

/**
 * Backfill helper for the pre-multi-user migration: if `gmSeats` is empty
 * (e.g. league SQLite written before phase 5's schema change), populate
 * from existing teams + the user's current tid. No-op if seats already
 * exist. Reads `gameAttributes.userTid` to pick the owner.
 *
 * Necessary because in the fake-indexeddb + SQLite snapshot model, the
 * `connectLeague` schema migrate hook (oldVersion < 72) never runs against
 * existing data — each process starts fake-indexeddb fresh at the latest
 * schema and pre-restores rows from the .sqlite into the already-current
 * schema, so old leagues end up with an empty `gmSeats` store.
 */
export const ensureGmSeatsPopulated = async (
	db: IDBPDatabase<LeagueDB>,
): Promise<{
	alreadyPopulated: boolean;
	written: number;
	userTidGuessed?: boolean;
}> => {
	const readTx = db.transaction("gmSeats", "readonly");
	const existing = await readTx.objectStore("gmSeats").count();
	if (existing > 0) {
		return { alreadyPopulated: true, written: 0 };
	}

	const gaTx = db.transaction("gameAttributes", "readonly");
	const userTidAttr = (await gaTx
		.objectStore("gameAttributes")
		.get("userTid")) as { value: unknown } | undefined;
	const rawUserTid = userTidAttr?.value;
	// `userTid` is stored as a history array `[{start, value}]` in modern
	// leagues, but legacy leagues may still hold a bare number — handle both.
	// If neither shape matches we fall back to tid 0 with a warning; that
	// silently mis-attributes a real user's team to AI control if tid 0 is
	// actually an AI team, so the caller (and operator log) should know.
	let userTid: number;
	let userTidGuessed = false;
	if (Array.isArray(rawUserTid)) {
		const last = rawUserTid.at(-1) as { value?: unknown } | undefined;
		if (typeof last?.value === "number") {
			userTid = last.value;
		} else {
			userTid = 0;
			userTidGuessed = true;
		}
	} else if (typeof rawUserTid === "number") {
		userTid = rawUserTid;
	} else {
		userTid = 0;
		userTidGuessed = true;
	}

	if (userTidGuessed) {
		console.warn(
			`[gmSeats] backfill could not read gameAttributes.userTid; ` +
				`assigning ownership to tid 0. If that team is an AI team in this ` +
				`league, the user will be locked out of their real team — set the ` +
				`correct seat manually.`,
		);
	}

	const written = await initGmSeats(db, userTid);
	return { alreadyPopulated: false, written, userTidGuessed };
};

/**
 * Move the owning userId from their current seat to `newTid`. The previous
 * seat (if any) goes back to AI (`userId: null`). The destination seat is
 * stamped with a fresh `joinedAt` and `active: true`.
 *
	 * Used when zengm changes `g.userTid` at runtime (`switchTeam`,
	 * expansion-draft selections) — the seats table must
 * stay in lockstep or the authorization check will refuse to let the user
 * operate on their newly-controlled team. Hooked centrally from
 * `server/worker/core/league/setGameAttributes.ts`.
 *
 * No-op if `userId` already owns `newTid`. If `userId` has no current seat
 * (e.g. new user joining a multi-GM league), this just claims `newTid`
 * without touching anything else.
 */
export const reseatUser = async (
	db: IDBPDatabase<LeagueDB>,
	userId: string,
	newTid: number,
): Promise<{ vacatedTid: number | null; claimedTid: number }> => {
	const tx = db.transaction("gmSeats", "readwrite");
	const store = tx.objectStore("gmSeats");
	let vacatedTid: number | null = null;
	let claimedAlready = false;
	for await (const cursor of store.iterate()) {
		const seat = cursor.value;
		if (seat.userId === userId) {
			if (seat.tid === newTid) {
				claimedAlready = true;
			} else {
				vacatedTid = seat.tid;
				await cursor.update({ ...seat, userId: null });
			}
		} else if (seat.tid === newTid) {
			await cursor.update({
				...seat,
				userId,
				joinedAt: Date.now(),
				active: true,
			});
			claimedAlready = true;
		}
	}
	await tx.done;
	if (!claimedAlready) {
		// `newTid` has no seat row yet (very rare — would only happen if the
		// teams set grew after init). Insert one in a fresh tx.
		const insertTx = db.transaction("gmSeats", "readwrite");
		await insertTx.objectStore("gmSeats").put({
			tid: newTid,
			userId,
			joinedAt: Date.now(),
			active: true,
		});
		await insertTx.done;
	}
	return { vacatedTid, claimedTid: newTid };
};
